/**
 * AskAudiaFAQModal - FAQ-specific Audia assistant
 * Focuses on operational guidance about the NewAudia platform.
 * Does NOT discuss inner workings or business practices.
 * Supports clickable navigation links in responses.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, X, Loader2 } from 'lucide-react';
import api from '../lib/api';

// Navigation link mappings for common pages
const PAGE_LINKS = {
  'reader home': '/home',
  'reader homepage': '/home',
  'homepage': '/home',
  'home page': '/home',
  'my library': '/reader/my-library',
  'library': '/reader/my-library',
  'pricing': '/pricing',
  'pricing page': '/pricing',
  'buy credits': '/pricing',
  'credit bundles': '/pricing',
  'subscriptions': '/pricing',
  'faq': '/faq',
  'frequently asked questions': '/faq',
  'contact': '/contact',
  'contact us': '/contact',
  'contact page': '/contact',
  'support': '/contact',
  'about': '/about',
  'about page': '/about',
  'about newaudia': '/about',
  'sign up': '/login?mode=register',
  'register': '/login?mode=register',
  'create account': '/login?mode=register',
  'sign in': '/login',
  'login': '/login',
  'author dashboard': '/author',
  'author console': '/author',
  'publisher dashboard': '/publisher',
  'creator dashboard': '/creator',
};

// Component to render message with clickable links
const MessageWithLinks = ({ content, onLinkClick }) => {
  const renderContent = () => {
    let processedContent = content;
    const links = [];
    
    Object.entries(PAGE_LINKS).forEach(([phrase, path]) => {
      const regex = new RegExp(`\\b(${phrase})\\b`, 'gi');
      if (regex.test(processedContent)) {
        links.push({ phrase, path });
      }
    });
    
    if (links.length === 0) {
      return <span className="whitespace-pre-wrap">{content}</span>;
    }
    
    const linkPhrases = links.map(l => l.phrase).join('|');
    const splitRegex = new RegExp(`(\\b(?:${linkPhrases})\\b)`, 'gi');
    const parts = content.split(splitRegex);
    
    return parts.map((part, index) => {
      const lowerPart = part.toLowerCase();
      const matchedLink = links.find(l => l.phrase.toLowerCase() === lowerPart);
      
      if (matchedLink) {
        return (
          <button
            key={index}
            onClick={() => onLinkClick(matchedLink.path)}
            className="text-purple-400 hover:text-purple-300 underline underline-offset-2 font-medium transition-colors"
          >
            {part}
          </button>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };
  
  return <p className="text-sm leading-relaxed">{renderContent()}</p>;
};

const AskAudiaFAQModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Audia, your NewAudia\u2122 guide. I can help you understand how to use the platform, navigate features, get started as a reader or author, and answer questions about how things work. What would you like to know?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const lastAssistantRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to top of latest assistant response, or bottom for user messages
  useEffect(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === 'assistant' && lastAssistantRef.current) {
      lastAssistantRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset chat when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([
        {
          role: 'assistant',
          content: "Hi! I'm Audia, your NewAudia\u2122 guide. I can help you understand how to use the platform, navigate features, get started as a reader or author, and answer questions about how things work. What would you like to know?"
        }
      ]);
      setInput('');
      setSuggestions([]);
    }
  }, [isOpen]);

  const handleLinkClick = (path) => {
    onClose();
    navigate(path);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setSuggestions([]);
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await api.post('/audia/faq-assistant', {
        message: userMessage,
        conversationHistory: messages.slice(-6)
      });

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response || "I'm sorry, I couldn't process that request. Please try again."
      }]);

      if (response.data.suggestions?.length > 0) {
        setSuggestions(response.data.suggestions);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('FAQ Assistant error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again in a moment, or browse the FAQ categories above for answers to common questions."
      }]);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionTap = (question) => {
    if (isLoading) return;
    setInput('');
    setSuggestions([]);
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setIsLoading(true);

    api.post('/audia/faq-assistant', {
      message: question,
      conversationHistory: messages.slice(-6)
    }).then(response => {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response || "I'm sorry, I couldn't process that. Please try again."
      }]);
      if (response.data.suggestions?.length > 0) {
        setSuggestions(response.data.suggestions);
      }
    }).catch(() => {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again in a moment."
      }]);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const suggestedQuestions = [
    "How do I get started?",
    "What are Reader Credits?",
    "How do I publish a book?",
    "Is NewAudia free?"
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full sm:max-w-lg h-[100dvh] sm:h-auto sm:max-h-[85vh] bg-gradient-to-br from-slate-900 to-slate-950 border-0 sm:border sm:border-purple-500/30 sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <img 
                src="/audia-avatar.jpg" 
                alt="Audia" 
                className="w-10 h-10 rounded-full border-2 border-purple-500/50 object-cover"
              />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-slate-900"></span>
            </div>
            <div>
              <h3 className="text-white font-semibold">Ask Audia</h3>
              <p className="text-slate-400 text-xs">Platform Guide</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
            data-testid="close-faq-audia-modal-x"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              ref={msg.role === 'assistant' && idx === messages.length - 1 ? lastAssistantRef : null}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-purple-600 text-white rounded-br-md'
                    : 'bg-slate-800 text-slate-200 rounded-bl-md border border-slate-700'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <MessageWithLinks content={msg.content} onLinkClick={handleLinkClick} />
                ) : (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-slate-800 text-slate-200 rounded-2xl rounded-bl-md px-4 py-3 border border-slate-700">
                <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestion Chips — show after responses OR initial tips at start */}
        {suggestions.length > 0 && !isLoading ? (
          <div data-testid="faq-audia-suggestions" className="px-3 pt-2 pb-1 border-t border-slate-700/50 bg-slate-900/60 flex-shrink-0">
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((q, idx) => (
                <button
                  key={idx}
                  data-testid={`faq-audia-suggestion-${idx}`}
                  onClick={() => handleSuggestionTap(q)}
                  className="px-2.5 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs text-slate-200 bg-slate-800 border border-slate-600/60 rounded-full hover:bg-purple-900/40 hover:border-purple-500/50 hover:text-purple-300 active:scale-95 transition-all duration-150 cursor-pointer leading-snug"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : messages.length === 1 ? (
          <div className="px-4 pb-2 flex-shrink-0">
            <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestedQuestions.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionTap(q)}
                  data-testid={`faq-audia-quick-${idx}`}
                  className="px-2.5 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full border border-slate-700 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Input Area */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-slate-700/50 bg-slate-900/50 flex-shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about using NewAudia..."
              className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
              disabled={isLoading}
              data-testid="faq-audia-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl px-4 py-2.5 disabled:opacity-50 transition-colors"
              data-testid="faq-audia-send-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            I can help with platform features and how-to questions
          </p>
        </form>

        {/* Close Chat Button */}
        <div className="px-4 pb-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full bg-slate-800/50 hover:bg-slate-800 text-gray-400 hover:text-white border border-slate-700/50 font-bold py-2.5 rounded-xl transition-colors"
            data-testid="faq-audia-close-chat-btn"
          >
            Close Chat
          </button>
        </div>
      </div>
    </div>
  );
};

export default AskAudiaFAQModal;
